import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { errorMessage } from "../api/client";
import { formatBrl, slugify, useCreateAppTask } from "../api/appTasks";
import type { EnvironmentTypeRead, TaskCategoryRead } from "../api/types";
import { CategoryPill, EnvPill, Switch, envColor } from "./AppTaskControls";

// "New task" modal. The id (slug) is derived from the EN title until the admin edits it by
// hand; it is immutable after creation because the app and recordings match on it.

interface Props {
  open: boolean;
  environments: EnvironmentTypeRead[];
  categories: TaskCategoryRead[];
  defaultEnvSlug: string;
  defaultCategoryId: string | null;
  existingIds: Set<string>;
  onClose: () => void;
  onCreated: (message: string) => void;
}

export default function AppTaskCreateDialog({
  open,
  environments,
  categories,
  defaultEnvSlug,
  defaultCategoryId,
  existingIds,
  onClose,
  onCreated,
}: Props) {
  const create = useCreateAppTask();
  const [envSlug, setEnvSlug] = useState(defaultEnvSlug);
  const [categoryId, setCategoryId] = useState("");
  const [pay, setPay] = useState("");
  const [title, setTitle] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [featured, setFeatured] = useState(false);
  const [active, setActive] = useState(true);

  const envCategories = useMemo(
    () => categories.filter((c) => c.environment_slug === envSlug),
    [categories, envSlug],
  );

  // reset on every open
  useEffect(() => {
    if (!open) return;
    setEnvSlug(defaultEnvSlug);
    const preset = categories.find((c) => c.id === defaultCategoryId);
    const first =
      preset ??
      categories.find((c) => c.environment_slug === defaultEnvSlug && c.is_active) ??
      categories.find((c) => c.environment_slug === defaultEnvSlug);
    setCategoryId(first?.id ?? "");
    setPay("");
    setTitle("");
    setTitleEn("");
    setId("");
    setIdTouched(false);
    setDescription("");
    setDescriptionEn("");
    setFeatured(false);
    setActive(true);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function pickEnv(slug: string) {
    setEnvSlug(slug);
    const first =
      categories.find((c) => c.environment_slug === slug && c.is_active) ??
      categories.find((c) => c.environment_slug === slug);
    setCategoryId(first?.id ?? "");
  }

  const duplicate = id !== "" && existingIds.has(id);
  const payNumber = pay.trim() === "" ? null : Number(pay);
  const payInvalid = payNumber != null && (!Number.isFinite(payNumber) || payNumber < 0);
  const cat = categories.find((c) => c.id === categoryId) ?? null;
  const env = environments.find((e) => e.slug === envSlug);
  const ready =
    !!id && !duplicate && !!categoryId && !payInvalid &&
    title.trim() !== "" && titleEn.trim() !== "" &&
    description.trim() !== "" && descriptionEn.trim() !== "";

  function submit() {
    if (!ready) return;
    create.mutate(
      {
        id,
        title: title.trim(),
        description: description.trim(),
        title_en: titleEn.trim(),
        description_en: descriptionEn.trim(),
        task_category_id: categoryId,
        payments_value: payNumber,
        is_featured: featured,
        is_active: active,
      },
      {
        onSuccess: (saved) => {
          onCreated(
            saved.is_active
              ? `“${saved.title}” created and published to the app`
              : `“${saved.title}” created (inactive)`,
          );
          onClose();
        },
      },
    );
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal at-modal" role="dialog" aria-modal="true" aria-label="New task">
        <div className="at-modal-head">
          <div>
            <h3 className="modal-title">New task</h3>
            <p className="muted small">
              Creates a row in <code>tasks</code>. The app lists it as soon as it is active.
            </p>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="at-modal-body at-form">
          <div className="at-field">
            <span className="field-label">Environment</span>
            <div className="at-radios">
              {environments.map((e) => (
                <label key={e.id} style={{ ["--sc" as string]: envColor(e.slug) }}>
                  <input
                    type="radio"
                    name="at-new-env"
                    value={e.slug}
                    checked={envSlug === e.slug}
                    onChange={() => pickEnv(e.slug)}
                  />
                  {e.name_en}
                  <small>{e.name}</small>
                </label>
              ))}
            </div>
          </div>

          <div className="at-grid2">
            <div className="at-field">
              <label className="field-label" htmlFor="at-n-cat">
                Category
              </label>
              <select
                id="at-n-cat"
                className="select-input"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                {envCategories.length === 0 && <option value="">No categories yet</option>}
                {envCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.name_en}
                    {c.is_active ? "" : " (inactive)"}
                  </option>
                ))}
              </select>
            </div>
            <div className="at-field">
              <label className="field-label" htmlFor="at-n-pay">
                Hourly rate (payments_value)
              </label>
              <div className={payInvalid ? "at-moneyin invalid" : "at-moneyin"}>
                <span>R$</span>
                <input
                  id="at-n-pay"
                  className="text-input"
                  type="number"
                  min={0}
                  step={0.5}
                  placeholder="0,00"
                  value={pay}
                  onChange={(e) => setPay(e.target.value)}
                />
                <span>/ hour</span>
              </div>
            </div>

            <div className="at-field at-full">
              <label className="field-label" htmlFor="at-n-title">
                Title <span className="at-lang">PT-BR</span>
              </label>
              <input
                id="at-n-title"
                className="text-input"
                placeholder="e.g. Limpar vitrine da loja"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
              <div className="at-hint">Shown in the app.</div>
            </div>
            <div className="at-field at-full">
              <label className="field-label" htmlFor="at-n-title-en">
                Title <span className="at-lang">EN</span>
              </label>
              <input
                id="at-n-title-en"
                className="text-input"
                placeholder="e.g. Clean the shop window"
                value={titleEn}
                onChange={(e) => {
                  setTitleEn(e.target.value);
                  if (!idTouched) setId(slugify(e.target.value));
                }}
              />
              <div className="at-hint">
                Canonical; stamped into recordings' <code>metadata.json</code>.
              </div>
            </div>
            <div className="at-field at-full">
              <label className="field-label" htmlFor="at-n-id">
                Id (slug)
              </label>
              <div className="at-slugin">
                <span>tasks.id</span>
                <input
                  id="at-n-id"
                  className="text-input"
                  placeholder="clean_shop_window"
                  value={id}
                  onChange={(e) => {
                    setIdTouched(true);
                    setId(slugify(e.target.value.replace(/-/g, "_")));
                  }}
                />
              </div>
              <div className="at-hint">
                Generated from the EN title. Immutable after creation: the app and recordings use it.
              </div>
              {duplicate && <div className="error small">A task with this id already exists.</div>}
            </div>
            <div className="at-field">
              <label className="field-label" htmlFor="at-n-desc">
                Description <span className="at-lang">PT-BR</span>
              </label>
              <textarea
                id="at-n-desc"
                className="text-input at-textarea"
                placeholder="Short instruction shown before recording"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="at-field">
              <label className="field-label" htmlFor="at-n-desc-en">
                Description <span className="at-lang">EN</span>
              </label>
              <textarea
                id="at-n-desc-en"
                className="text-input at-textarea"
                placeholder="Short instruction, canonical"
                value={descriptionEn}
                onChange={(e) => setDescriptionEn(e.target.value)}
              />
            </div>
          </div>

          <div className="at-grid2">
            <div className="at-rowfield">
              <div>
                <div className="at-rowfield-t">Highlight in the app</div>
                <div className="at-rowfield-d">Highlight badge in the list.</div>
              </div>
              <Switch checked={featured} onChange={setFeatured} label="Highlight in the app" />
            </div>
            <div className="at-rowfield">
              <div>
                <div className="at-rowfield-t">Create active</div>
                <div className="at-rowfield-d">Turn off to publish later.</div>
              </div>
              <Switch checked={active} onChange={setActive} label="Create active" />
            </div>
          </div>

          <div className="at-preview">
            <span className="muted small">Preview:</span>
            {env && <EnvPill slug={env.slug} name={env.name_en} />}
            <CategoryPill cat={cat} />
            <b>{title.trim() || "—"}</b>
            {featured && (
              <span className="at-pill" style={{ ["--sc" as string]: "var(--amber)" }}>
                ★ highlight
              </span>
            )}
            <span className={payNumber == null ? "at-money unset" : "at-money"}>
              {payNumber == null ? "no rate" : `${formatBrl(payNumber)}/h`}
            </span>
          </div>

          {create.isError && <p className="error">{errorMessage(create.error)}</p>}
        </div>

        <div className="modal-actions">
          <button className="btn ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={!ready || create.isPending}>
            {create.isPending ? "Creating…" : "Create task"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
